import { useEffect } from 'react';

// ============================================================
// PRODUCT CATALOG — single source of truth for tracking values
// All prices in US Dollars (USD).
// ============================================================
export type ProductKey = 'ff365';
type Product = {
  id: string;
  name: string;
  value: number;
  currency: 'USD';
  checkoutUrl: string;
};
export const PRODUCTS: Record<ProductKey, Product> = {
  ff365: {
    id: 'functional-fitness-365',
    name: 'Functional Fitness 365',
    value: 19,
    currency: 'USD',
    checkoutUrl: 'https://functional-fitness-365.impultienda.ar/checkout'
  }
};
export const PIXEL_ID = '1119692460632479';
type FbqFunction = ((...args: unknown[]) => void) & {
  callMethod?: (...args: unknown[]) => void;
  queue: unknown[];
  push: FbqFunction;
  loaded: boolean;
  version: string;
};

/**
 * Dedupe state lives on `window` (not in module scope) so it survives
 * hot reloads, re-mounts and React StrictMode double effects.
 */
type PixelState = {
  initialized: boolean;
  viewContentSent: boolean;
  lastCheckoutAt: number;
};
declare global {
  interface Window {
    fbq?: FbqFunction;
    _fbq?: FbqFunction;
    __ff365Pixel?: PixelState;
  }
}
const PURCHASE_STORAGE_KEY = 'ff365_tracked_purchases';
const CHECKOUT_DEBOUNCE_MS = 3000;
function getState(): PixelState {
  if (!window.__ff365Pixel) {
    window.__ff365Pixel = {
      initialized: false,
      viewContentSent: false,
      lastCheckoutAt: 0
    };
  }
  return window.__ff365Pixel;
}

// ============================================================
// Pixel bootstrap — official Meta snippet, typed and idempotent.
// ============================================================
function ensurePixel(): FbqFunction | null {
  if (typeof window === 'undefined') return null;
  if (!window.fbq) {
    const n = function (...args: unknown[]) {
      if (n.callMethod) n.callMethod.apply(n, args);else n.queue.push(args);
    } as FbqFunction;
    n.push = n;
    n.loaded = true;
    n.version = '2.0';
    n.queue = [];
    window.fbq = n;
    if (!window._fbq) window._fbq = n;
  }
  if (!document.getElementById('fb-pixel-script')) {
    const script = document.createElement('script');
    script.id = 'fb-pixel-script';
    script.async = true;
    script.src = 'https://connect.facebook.net/en_US/fbevents.js';
    document.head.appendChild(script);
  }
  const state = getState();
  if (!state.initialized) {
    state.initialized = true;
    // Disable automatic events (auto "SubscribedButtonClick" etc.) so only
    // the events below are sent — no extra / duplicated clicks in Events Manager.
    window.fbq('set', 'autoConfig', false, PIXEL_ID);
    window.fbq('init', PIXEL_ID);
    window.fbq('track', 'PageView', {}, {
      eventID: eventId('PageView')
    });
  }
  return window.fbq;
}
function eventId(name: string) {
  const rand = typeof crypto !== 'undefined' && 'randomUUID' in crypto ? crypto.randomUUID() : `${Date.now()}.${Math.random().toString(36).slice(2, 10)}`;
  return `${name}.${rand}`;
}
const productPayload = (p: Product, value: number) => ({
  value,
  currency: p.currency,
  content_ids: [p.id],
  content_name: p.name,
  content_type: 'product',
  contents: [{
    id: p.id,
    quantity: 1,
    item_price: value
  }],
  num_items: 1
});

// ============================================================
// Tracking helpers
// ============================================================
export const trackViewContent = (productKey: ProductKey) => {
  if (typeof window === 'undefined') return;
  const state = getState();
  if (state.viewContentSent) return;
  const fbq = ensurePixel();
  if (!fbq) return;
  state.viewContentSent = true;
  const p = PRODUCTS[productKey];
  fbq('track', 'ViewContent', productPayload(p, p.value), {
    eventID: eventId('ViewContent')
  });
};
export const trackInitiateCheckout = (productKey: ProductKey) => {
  if (typeof window === 'undefined') return;
  const state = getState();
  const now = Date.now();
  // Ignore double taps / double click handlers within the debounce window
  if (now - state.lastCheckoutAt < CHECKOUT_DEBOUNCE_MS) return;
  const fbq = ensurePixel();
  if (!fbq) return;
  state.lastCheckoutAt = now;
  const p = PRODUCTS[productKey];
  fbq('track', 'InitiateCheckout', productPayload(p, p.value), {
    eventID: eventId('InitiateCheckout')
  });
};
function readTrackedPurchases(): string[] {
  try {
    const raw = window.localStorage.getItem(PURCHASE_STORAGE_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.filter((v): v is string => typeof v === 'string') : [];
  } catch {
    return [];
  }
}

/**
 * Fires Purchase once per order. If the return URL carries an order id
 * (order_id / order / transaction_id), it's used as the eventID so Meta
 * also dedupes it server-side; repeat visits with the same id are skipped.
 */
export const trackPurchase = (productKey: ProductKey, overrideValue?: number, orderId?: string) => {
  if (typeof window === 'undefined') return;
  const key = orderId ? `order:${orderId}` : `session:${sessionKey()}`;
  const tracked = readTrackedPurchases();
  if (tracked.includes(key)) return;
  const fbq = ensurePixel();
  if (!fbq) return;
  const p = PRODUCTS[productKey];
  const value = typeof overrideValue === 'number' && Number.isFinite(overrideValue) && overrideValue > 0 ? overrideValue : p.value;
  fbq('track', 'Purchase', productPayload(p, value), {
    eventID: orderId ? `Purchase.${orderId}` : eventId('Purchase')
  });
  try {
    window.localStorage.setItem(PURCHASE_STORAGE_KEY, JSON.stringify([...tracked, key].slice(-20)));
  } catch {
    // Storage unavailable — the URL cleanup below still prevents refresh repeats.
  }
};
function sessionKey() {
  try {
    let id = window.sessionStorage.getItem('ff365_session');
    if (!id) {
      id = eventId('s');
      window.sessionStorage.setItem('ff365_session', id);
    }
    return id;
  } catch {
    return 'nosession';
  }
}

/**
 * Navigates once the pixel has had a moment to send queued events.
 * If the Meta script is still loading, waits a little longer (max ~900ms)
 * so the InitiateCheckout event isn't lost on page unload.
 */
export const navigateAfterTracking = (href: string) => {
  if (typeof window === 'undefined') return;
  const started = Date.now();
  const go = () => {
    const loaded = Boolean(window.fbq?.callMethod);
    if (loaded || Date.now() - started > 900) {
      window.setTimeout(() => window.location.assign(href), loaded ? 200 : 0);
      return;
    }
    window.setTimeout(go, 100);
  };
  go();
};

// ============================================================
// MetaPixel component — initializes the pixel and handles
// the Purchase return URL (?purchase=1[&value=...][&order_id=...])
// ============================================================
export function MetaPixel() {
  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      ensurePixel();
    } catch {
      // Tracking must never break the landing page.
    }

    // ViewContent once, when the offer is actually seen
    const pricing = document.getElementById('pricing');
    let observer: IntersectionObserver | null = null;
    if (pricing && 'IntersectionObserver' in window && !getState().viewContentSent) {
      observer = new IntersectionObserver(([entry]) => {
        if (entry.isIntersecting) {
          trackViewContent('ff365');
          observer?.disconnect();
        }
      }, {
        threshold: 0.25
      });
      observer.observe(pricing);
    }
    try {
      const params = new URLSearchParams(window.location.search);
      if (params.get('purchase')) {
        const valueParam = params.get('value');
        const overrideValue = valueParam ? Number(valueParam) : undefined;
        const orderId = params.get('order_id') || params.get('order') || params.get('transaction_id') || undefined;
        trackPurchase('ff365', overrideValue, orderId);
        const url = new URL(window.location.href);
        ['purchase', 'value', 'order_id', 'order', 'transaction_id'].forEach(k => url.searchParams.delete(k));
        window.history.replaceState(window.history.state, '', url.toString());
      }
    } catch {
      // Tracking must never break the landing page.
    }
    return () => observer?.disconnect();
  }, []);
  return null;
}